import React, { useState, useEffect } from 'react';
import {
    RiBookLine,
    RiDeleteBinLine,
    RiUploadCloud2Line,
    RiLoader4Line,
    RiHashtag,
    RiTimeLine,
    RiUser6Line,
     RiFileList3Line
} from 'react-icons/ri';
import { motion, AnimatePresence } from 'framer-motion';
import { API_BASE_URL, ASSET_URL } from '../../config';

const ManagePublications = () => {
    const [dbCategories, setDbCategories] = useState(["Marine Biology", "Conservation", "Ecology", "Policy"]);
    const [publications, setPublications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isCustomCategory, setIsCustomCategory] = useState(false);
    const [uploadMode, setUploadMode] = useState('upload'); // 'upload' or 'url'
    const [selectedFile, setSelectedFile] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [message, setMessage] = useState({ type: '', text: '' });

    // Form state
    const [formData, setFormData] = useState({
        category: 'Conservation',
        title: '',
        author: '',
        doi: '',
        publication_date: '',
        description: '',
        file_url: '',
        tags: []
    });
    const [currentTag, setCurrentTag] = useState('');

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        setLoading(true);
        try {
            // Fetch categories
            const catRes = await fetch(`${API_BASE_URL}/archive/get_categories.php`);
            const catData = await catRes.json();
            if (catRes.ok && catData.records?.length > 0) {
                setDbCategories(catData.records);
            }

            // Fetch publications
            const pubRes = await fetch(`${API_BASE_URL}/archive/read.php`);
            const pubData = await pubRes.json();
            if (pubRes.ok) {
                setPublications(pubData.records || []);
            }
        } catch (error) {
            console.error('Error fetching publications:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setSelectedFile(file);
        }
    };

    const addTag = () => {
        if (currentTag && !formData.tags.includes(currentTag)) {
            setFormData({ ...formData, tags: [...formData.tags, currentTag] });
            setCurrentTag('');
        }
    };

    const removeTag = (tagToRemove) => {
        setFormData({ ...formData, tags: formData.tags.filter(t => t !== tagToRemove) });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setMessage({ type: '', text: '' });

        let finalFileUrl = formData.file_url;

        // 1. Upload file if in upload mode and file selected
        if (uploadMode === 'upload' && selectedFile) {
            const uploadFormData = new FormData();
            uploadFormData.append('file', selectedFile);

            try {
                const uploadResponse = await fetch(`${API_BASE_URL}/upload.php`, {
                    method: 'POST',
                    body: uploadFormData
                });
                const uploadData = await uploadResponse.json();

                if (uploadResponse.ok) {
                    finalFileUrl = uploadData.url;
                } else {
                    setMessage({ type: 'error', text: uploadData.message || 'Failed to upload document.' });
                    setIsSubmitting(false);
                    return;
                }
            } catch (error) {
                setMessage({ type: 'error', text: 'Upload failed due to network error.' });
                setIsSubmitting(false);
                return;
            }
        }

        try {
            const url = editingId ? `${API_BASE_URL}/archive/update.php` : `${API_BASE_URL}/archive/create.php`;
            const response = await fetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ...formData, file_url: finalFileUrl, id: editingId })
            });

            if (response.ok) {
                setMessage({ type: 'success', text: editingId ? 'Publication updated!' : 'Publication archived successfully!' });
                setFormData({
                    category: 'Conservation',
                    title: '',
                    author: '',
                    doi: '',
                    publication_date: '',
                    description: '',
                    file_url: '',
                    tags: []
                });
                setSelectedFile(null);
                setEditingId(null);
                setIsCustomCategory(false);
                fetchData();
            } else {
                const data = await response.json();
                setMessage({ type: 'error', text: data.message || 'Failed to process publication.' });
            }
        } catch (error) {
            setMessage({ type: 'error', text: 'A network error occurred.' });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleEdit = (pub) => {
        setEditingId(pub.id);
        const isCustom = !dbCategories.includes(pub.category);
        setIsCustomCategory(isCustom);

        // Detect if file_url is a full URL or a local path
        const isExternal = pub.file_url?.startsWith('http');
        setUploadMode(isExternal ? 'url' : 'upload');

        setFormData({
            category: pub.category,
            title: pub.title,
            author: pub.author,
            doi: pub.doi || '',
            publication_date: pub.publication_date,
            description: pub.description,
            file_url: pub.file_url,
            tags: pub.tags || []
        });
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleCancelEdit = () => {
        setEditingId(null);
        setFormData({
            category: 'Conservation',
            title: '',
            author: '',
            doi: '',
            publication_date: '',
            description: '',
            file_url: '',
            tags: []
        });
    };

    const handleDelete = async (id, title) => {
        if (!window.confirm(`Are you sure you want to remove "${title}"?`)) return;

        try {
            const response = await fetch(`${API_BASE_URL}/archive/delete.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ id })
            });

            if (response.ok) {
                fetchData();
            } else {
                alert("Failed to remove publication.");
            }
        } catch (error) {
            console.error('Error removing publication:', error);
        }
    };

    return (
        <div className="max-w-[1400px] mx-auto pb-20">
            <div className="mb-10">
                <h2 className="text-4xl font-black text-slate-900 tracking-tight">Scientific Archives</h2>
                <p className="text-slate-500 font-medium mt-2">Manage research papers, white papers, and global policies</p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
                {/* Form Section */}
                <div className="lg:col-span-12 xl:col-span-4">
                    <div className="bg-white p-8 rounded-[2.5rem] shadow-[0_20px_50px_rgba(0,0,0,0.04)] border border-slate-100 italic sticky top-10">
                        <div className="flex items-center gap-4 mb-8">
                            <div className="w-14 h-14 rounded-2xl bg-emerald-600 flex items-center justify-center text-white shadow-xl shadow-emerald-900/20">
                                <RiBookLine size={28} />
                            </div>
                            <div>
                                <h3 className="font-black text-slate-900 text-lg leading-tight">{editingId ? 'Modify Paper' : 'Archive Paper'}</h3>
                                <p className="text-[10px] text-emerald-600 font-black uppercase tracking-[0.2em] mt-1">{editingId ? 'Update Entry' : 'New Publication'}</p>
                            </div>
                        </div>

                        <form onSubmit={handleSubmit} className="space-y-6">
                            {editingId && (
                                <div className="bg-emerald-50 p-4 rounded-2xl flex items-center justify-between">
                                    <span className="text-[10px] font-black text-emerald-600 uppercase tracking-widest px-1">Editing Mode Active</span>
                                    <button type="button" onClick={handleCancelEdit} className="text-[10px] font-black text-slate-400 uppercase tracking-widest hover:text-red-500">Cancel</button>
                                </div>
                            )}
                            <div className="grid grid-cols-2 gap-4">
                                <div className="col-span-2">
                                    <label className="block text-[10px] font-black uppercase tracking-widest text-slate-400 mb-2 ml-1">Title of Work</label>
                                    <input
                                        type="text"
                                        placeholder="Enter publication title..."
                                        value={formData.title}
                                        onChange={e => setFormData({ ...formData, title: e.target.value })}
                                        className="w-full p-4 bg-slate-50 rounded-2xl border-none text-sm font-bold focus:ring-2 focus:ring-emerald-500"
                                        required
                                    />
                                </div>
                                <div className="col-span-2">
                                    <label className="block text-[10px] font-black uppercase tracking-widest text-slate-400 mb-2 ml-1">DOI Link / Identifier</label>
                                    <input
                                        type="text"
                                        placeholder="https://doi.org/..."
                                        value={formData.doi}
                                        onChange={e => setFormData({ ...formData, doi: e.target.value })}
                                        className="w-full p-4 bg-slate-50 rounded-2xl border-none text-sm font-bold focus:ring-2 focus:ring-emerald-500"
                                    />
                                </div>
                                <div className="col-span-2 sm:col-span-1">
                                    <label className="block text-[10px] font-black uppercase tracking-widest text-slate-400 mb-2 ml-1">Principal Author</label>
                                    <input
                                        type="text"
                                        placeholder="Dr. Researcher..."
                                        value={formData.author}
                                        onChange={e => setFormData({ ...formData, author: e.target.value })}
                                        className="w-full p-4 bg-slate-50 rounded-2xl border-none text-sm font-bold focus:ring-2 focus:ring-emerald-500"
                                        required
                                    />
                                </div>
                                <div className="col-span-2 sm:col-span-1">
                                    <label className="block text-[10px] font-black uppercase tracking-widest text-slate-400 mb-2 ml-1">Release Date</label>
                                    <input
                                        type="text"
                                        placeholder="Jan 2026"
                                        value={formData.publication_date}
                                        onChange={e => setFormData({ ...formData, publication_date: e.target.value })}
                                        className="w-full p-4 bg-slate-50 rounded-2xl border-none text-sm font-bold focus:ring-2 focus:ring-emerald-500"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-[10px] font-black uppercase tracking-widest text-slate-400 mb-2 ml-1">Scientific Domain</label>
                                <div className="flex flex-wrap gap-2 mb-3">
                                    {dbCategories.map(cat => (
                                        <button
                                            key={cat}
                                            type="button"
                                            onClick={() => {
                                                setFormData({ ...formData, category: cat });
                                                setIsCustomCategory(false);
                                            }}
                                            className={`px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${!isCustomCategory && formData.category === cat
                                                ? 'bg-emerald-600 text-white shadow-lg shadow-emerald-900/20'
                                                : 'bg-slate-50 text-slate-400 hover:bg-slate-100'
                                                }`}
                                        >
                                            {cat}
                                        </button>
                                    ))}
                                    <button
                                        type="button"
                                        onClick={() => setIsCustomCategory(true)}
                                        className={`px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${isCustomCategory
                                            ? 'bg-emerald-600 text-white shadow-lg shadow-emerald-900/20'
                                            : 'bg-emerald-50 text-emerald-600 hover:bg-emerald-100'
                                            }`}
                                    >
                                        + Custom
                                    </button>
                                </div>
                                {isCustomCategory && (
                                    <motion.input
                                        initial={{ opacity: 0, y: -10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        type="text"
                                        placeholder="Type new category..."
                                        value={formData.category}
                                        onChange={e => setFormData({ ...formData, category: e.target.value })}
                                        className="w-full p-3 bg-emerald-50 rounded-xl border-none text-xs font-bold focus:ring-2 focus:ring-emerald-500 placeholder:text-emerald-200"
                                    />
                                )}
                            </div>

                            <div>
                                <label className="block text-[10px] font-black uppercase tracking-widest text-slate-400 mb-2 ml-1">Abstract / Description</label>
                                <textarea
                                    value={formData.description}
                                    onChange={e => setFormData({ ...formData, description: e.target.value })}
                                    placeholder="Brief summary of findings..."
                                    className="w-full p-4 bg-slate-50 rounded-2xl border-none text-sm font-bold h-32 focus:ring-2 focus:ring-emerald-500"
                                />
                            </div>

                            <div>
                                <div className="flex items-center justify-between mb-2 ml-1">
                                    <label className="text-[10px] font-black uppercase tracking-widest text-slate-400">Document Asset</label>
                                    <div className="flex bg-slate-50 p-1 rounded-lg">
                                        <button
                                            type="button"
                                            onClick={() => setUploadMode('upload')}
                                            className={`px-3 py-1 rounded-md text-[9px] font-black uppercase tracking-widest transition-all ${uploadMode === 'upload' ? 'bg-white text-emerald-600 shadow-sm' : 'text-slate-400'}`}
                                        >
                                            File
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setUploadMode('url')}
                                            className={`px-3 py-1 rounded-md text-[9px] font-black uppercase tracking-widest transition-all ${uploadMode === 'url' ? 'bg-white text-emerald-600 shadow-sm' : 'text-slate-400'}`}
                                        >
                                            Link
                                        </button>
                                    </div>
                                </div>

                                {uploadMode === 'upload' ? (
                                    <>
                                        <input
                                            type="file"
                                            onChange={handleFileChange}
                                            accept=".pdf,.doc,.docx"
                                            className="w-full text-xs text-slate-400 file:mr-4 file:py-3 file:px-6 file:rounded-xl file:border-0 file:text-[10px] file:font-black file:uppercase file:tracking-widest file:bg-slate-900 file:text-white hover:file:bg-slate-800"
                                        />
                                        {selectedFile && <p className="mt-2 text-[10px] text-emerald-600 font-bold uppercase tracking-widest">Selected: {selectedFile.name}</p>}
                                        {editingId && !selectedFile && formData.file_url && (
                                            <p className="mt-2 text-[9px] text-slate-400 font-medium">Current file: {formData.file_url.split('/').pop()}</p>
                                        )}
                                    </>
                                ) : (
                                    <input
                                        type="url"
                                        placeholder="Enter research link (https://...)"
                                        value={formData.file_url}
                                        onChange={e => setFormData({ ...formData, file_url: e.target.value })}
                                        className="w-full p-4 bg-slate-50 rounded-2xl border-none text-sm font-bold focus:ring-2 focus:ring-emerald-500"
                                    />
                                )}
                            </div>

                            <div>
                                <label className="block text-[10px] font-black uppercase tracking-widest text-slate-400 mb-2 ml-1">Tags (Max 3)</label>
                                <div className="flex gap-2 mb-3">
                                    <input
                                        type="text"
                                        placeholder="Add tag..."
                                        value={currentTag}
                                        onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                                        onChange={e => setCurrentTag(e.target.value)}
                                        className="flex-1 p-3 bg-slate-50 rounded-xl border-none text-xs font-bold focus:ring-2 focus:ring-emerald-500"
                                    />
                                    <button
                                        type="button"
                                        onClick={addTag}
                                        className="p-3 bg-slate-900 text-white rounded-xl hover:bg-slate-800"
                                    >
                                        <RiHashtag />
                                    </button>
                                </div>
                                <div className="flex flex-wrap gap-2">
                                    {formData.tags.map(tag => (
                                        <span key={tag} className="flex items-center gap-1 bg-emerald-50 text-emerald-600 px-3 py-1 rounded-lg text-[9px] font-black uppercase tracking-widest">
                                            #{tag}
                                            <button type="button" onClick={() => removeTag(tag)} className="hover:text-red-500">×</button>
                                        </span>
                                    ))}
                                </div>
                            </div>

                            <AnimatePresence>
                                {message.text && (
                                    <motion.div
                                        initial={{ opacity: 0, height: 0 }}
                                        animate={{ opacity: 1, height: 'auto' }}
                                        exit={{ opacity: 0, height: 0 }}
                                        className={`p-4 rounded-2xl text-xs font-bold ${message.type === 'success' ? 'bg-emerald-50 text-emerald-600' : 'bg-red-50 text-red-600'}`}
                                    >
                                        {message.text}
                                    </motion.div>
                                )}
                            </AnimatePresence>

                            <button
                                disabled={isSubmitting}
                                className="w-full bg-slate-900 text-white font-black py-4 rounded-2xl text-xs uppercase tracking-[0.4em] hover:bg-slate-800 transition-all shadow-xl shadow-slate-900/20 active:scale-95 disabled:opacity-50 flex items-center justify-center gap-2"
                            >
                                {editingId ? 'Update Knowledge Asset' : 'Archiving Knowledge'}
                            </button>
                            {editingId && (
                                <button
                                    type="button"
                                    onClick={handleCancelEdit}
                                    className="w-full bg-slate-50 text-slate-400 font-black py-4 rounded-2xl text-xs uppercase tracking-[0.4em] hover:bg-slate-100 transition-all mt-4"
                                >
                                    Cancel Changes
                                </button>
                            )}
                        </form>
                    </div>
                </div>

                {/* Archive List Section */}
                <div className="lg:col-span-12 xl:col-span-8">
                    <div className="bg-white rounded-[2.5rem] shadow-[0_20px_50px_rgba(0,0,0,0.04)] border border-slate-100 overflow-hidden">
                        <div className="p-8 border-b border-slate-50 flex items-center justify-between">
                            <h3 className="font-black text-slate-900 uppercase tracking-[0.2em] text-xs">Scientific Library</h3>
                            <span className="bg-slate-50 px-4 py-2 rounded-full text-[10px] font-black text-slate-400 tracking-widest">{publications.length} Papers Archived</span>
                        </div>

                        <div className="divide-y divide-slate-50">
                            {loading ? (
                                <div className="flex justify-center py-24 text-emerald-500">
                                    <RiLoader4Line className="animate-spin text-4xl" />
                                </div>
                            ) : publications.length === 0 ? (
                                <div className="py-24 text-center">
                                    <RiFileList3Line className="mx-auto text-4xl text-slate-200 mb-4" />
                                    <p className="text-slate-400 font-medium">No knowledge assets found in the archive.</p>
                                </div>
                            ) : (
                                publications.map((pub) => (
                                    <motion.div
                                        key={pub.id}
                                        layout
                                        className="p-8 flex items-start justify-between group hover:bg-slate-50/50 transition-colors"
                                    >
                                        <div className="flex-1">
                                            <div className="flex items-center gap-3 mb-3">
                                                <span className="bg-emerald-50 text-emerald-600 text-[9px] font-black px-2 py-0.5 rounded-full uppercase tracking-widest">{pub.category}</span>
                                                <span className="flex items-center gap-1 text-[10px] font-medium text-slate-400">
                                                    <RiTimeLine /> {pub.publication_date}
                                                </span>
                                            </div>
                                            <h4 className="text-lg font-bold text-slate-900 mb-2 group-hover:text-emerald-700 transition-colors">{pub.title}</h4>

                                            <div className="flex items-center gap-4 text-xs font-semibold text-slate-500 mb-4">
                                                <span className="flex items-center gap-1"><RiUser6Line className="text-emerald-500" /> {pub.author}</span>
                                                {pub.doi && <span className="text-[10px] text-slate-400 font-medium">DOI: {pub.doi}</span>}
                                                {pub.tags && (
                                                    <div className="flex gap-2">
                                                        {pub.tags.map(tag => (
                                                            <span key={tag} className="text-slate-300">#{tag}</span>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>

                                            {pub.file_url && (
                                                <a
                                                    href={`${ASSET_URL}${pub.file_url}`}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    className="inline-flex items-center gap-2 text-[10px] font-black text-emerald-600 uppercase tracking-widest hover:text-emerald-700"
                                                >
                                                    View Document Asset <RiUploadCloud2Line />
                                                </a>
                                            )}
                                        </div>

                                        <div className="flex gap-2">
                                            <button
                                                onClick={() => handleEdit(pub)}
                                                className="p-4 text-slate-300 hover:text-emerald-500 hover:bg-emerald-50 rounded-2xl transition-all"
                                                title="Edit Publication"
                                            >
                                                <RiFileList3Line size={20} />
                                            </button>
                                            <button
                                                onClick={() => handleDelete(pub.id, pub.title)}
                                                className="p-4 text-slate-300 hover:text-red-500 hover:bg-red-50 rounded-2xl transition-all"
                                                title="Remove from Archive"
                                            >
                                                <RiDeleteBinLine size={20} />
                                            </button>
                                        </div>
                                    </motion.div>
                                ))
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ManagePublications;
